/**
 * Small Power Tab — orchestrator.
 *
 * Receptacles, lighting fixtures and switches for an electrical / service
 * room. Auto-installs a starter layout per NEC or CEC code rules, then lets
 * the user edit each item manually.
 *
 * Heavy UI (settings grid, code refs, item tables, plan preview) lives in
 * ./smallpower/* — this file owns state + API calls.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Lightning, Lightbulb, Trash, Robot, BookOpen, Power,
} from "@phosphor-icons/react";

import { SummaryCard, Section, ItemTable } from "./smallpower/parts";
import SmallPowerPlan from "./smallpower/SmallPowerPlan";
import SmallPower3D from "./smallpower/SmallPower3D";
import CodeRefsPanel from "./smallpower/CodeRefsPanel";
import AutoInstallSettings from "./smallpower/AutoInstallSettings";

const EMPTY = {
  code: "NEC", receptacles: [], luminaires: [], switches: [],
  spec: {}, citations: [], notes: [], summary: {},
};

const RECEP_COLS = [
  { key: "tag",       label: "Tag",       width: "w-28" },
  { key: "type",      label: "Type",      width: "min-w-[180px]", isTypeSelect: true },
  { key: "amps",      label: "A",         width: "w-16",  type: "number" },
  { key: "volts",     label: "V",         width: "w-16",  type: "number" },
  { key: "x_in",      label: 'X "',       width: "w-20",  type: "number" },
  { key: "y_in",      label: 'Y "',       width: "w-20",  type: "number" },
  { key: "mount_in",  label: "AFF \"",    width: "w-20",  type: "number" },
  { key: "rule",      label: "Rule",      width: "min-w-[220px]" },
];

const LUM_COLS = [
  { key: "tag",       label: "Tag",       width: "w-28" },
  { key: "type",      label: "Type",      width: "min-w-[220px]", isTypeSelect: true },
  { key: "lumens",    label: "Lumens",    width: "w-24", type: "number" },
  { key: "watts",     label: "W",         width: "w-16", type: "number" },
  { key: "x_in",      label: 'X "',       width: "w-20", type: "number" },
  { key: "y_in",      label: 'Y "',       width: "w-20", type: "number" },
  { key: "mount_in",  label: "Mount \"",  width: "w-20", type: "number" },
  { key: "rule",      label: "Rule",      width: "min-w-[220px]" },
];

const SW_COLS = [
  { key: "tag",       label: "Tag",       width: "w-28" },
  { key: "type",      label: "Type",      width: "min-w-[200px]", isTypeSelect: true },
  { key: "amps",      label: "A",         width: "w-16", type: "number" },
  { key: "volts",     label: "V",         width: "w-16", type: "number" },
  { key: "x_in",      label: 'X "',       width: "w-20", type: "number" },
  { key: "y_in",      label: 'Y "',       width: "w-20", type: "number" },
  { key: "mount_in",  label: "AFF \"",    width: "w-20", type: "number" },
  { key: "rule",      label: "Rule",      width: "min-w-[200px]" },
];

const NUMERIC_FIELDS = ["x_in","y_in","mount_in","amps","volts","lumens","watts"];

export default function SmallPowerTab({ project, projectId, update }) {
  const [layout, setLayout]     = useState(EMPTY);
  const [opts, setOpts]         = useState({ receptacle_types: [], luminaire_types: [], switch_types: [] });
  const [code, setCode]         = useState(project.small_power?.code || "NEC");
  const [spacing, setSpacing]   = useState(12);
  const [targetFc, setTargetFc] = useState(50);
  const [withinFt, setWithinFt] = useState(25);
  const [mountIn, setMountIn]   = useState(18);
  const [replace, setReplace]   = useState(true);
  const [busy, setBusy]         = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  // v2.27 — preview mode toggles between the 2D plan SVG and the 3D Canvas.
  const [previewMode, setPreviewMode] = useState("2d");

  const roomReady = !!(project.length_in && project.width_in);

  useEffect(() => {
    (async () => {
      try {
        const [layoutRes, optsRes] = await Promise.all([
          api.get(`/projects/${projectId}/small-power`),
          api.get(`/small-power/options`),
        ]);
        setLayout(layoutRes.data || EMPTY);
        setOpts(optsRes.data || { receptacle_types: [], luminaire_types: [], switch_types: [] });
        if (layoutRes.data?.code) setCode(layoutRes.data.code);
      } catch {
        toast.error("Failed to load small-power data");
      }
    })();
  }, [projectId]);

  // Sync local layout when project.small_power changes externally (e.g. undo/redo).
  useEffect(() => {
    if (project?.small_power !== undefined && project?.small_power !== null) {
      setLayout(project.small_power);
      if (project.small_power.code) setCode(project.small_power.code);
    } else if (project?.small_power === null) {
      setLayout(EMPTY);
    }
  }, [project?.small_power]);

  const runAutoInstall = async () => {
    if (!roomReady) {
      toast.error("Set room length × width on the Size tab first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/small-power/auto-install`, {
        code,
        max_recept_spacing_ft: spacing,
        target_fc: targetFc,
        service_recept_within_ft: withinFt,
        mount_height_in: mountIn,
        replace,
      });
      setLayout(data);
      update && update({ small_power: data });
      toast.success(`Installed ${data.summary.receptacle_count} receptacle(s), ${data.summary.luminaire_count} luminaire(s), ${data.summary.switch_count} switch(es)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-install failed");
    } finally { setBusy(false); }
  };

  const saveLayout = async (next) => {
    // Route through the parent `update()` so the change participates in
    // the project-level undo system, AND persist immediately via the
    // dedicated endpoint for snappy UX.
    update && update({ small_power: next });
    try {
      const { data } = await api.put(`/projects/${projectId}/small-power`, next);
      setLayout(data);
      update && update({ small_power: data });
    } catch {
      toast.error("Save failed");
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Remove all small-power items? This cannot be undone.")) return;
    try {
      await api.delete(`/projects/${projectId}/small-power`);
      setLayout(EMPTY);
      update && update({ small_power: null });
      toast.success("Small-power layout cleared");
    } catch { toast.error("Clear failed"); }
  };

  const updateBucket = (bucket, items) => {
    const next = { ...layout, [bucket]: items };
    setLayout(next);
    saveLayout(next);
  };

  const addRow = (bucket) => {
    const id = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())).slice(0, 12);
    let row;
    if (bucket === "receptacles") {
      const t = opts.receptacle_types?.[0];
      row = {
        id, tag: `R-${(layout.receptacles.length + 1).toString().padStart(2, "0")}`,
        type: t?.id || "duplex_20a_125v", label: t?.label || "Duplex 20A",
        amps: t?.amps || 20, volts: t?.volts || 125,
        x_in: 12, y_in: 12, mount_in: mountIn, wall: "back", rule: "manual",
      };
    } else if (bucket === "luminaires") {
      const t = opts.luminaire_types?.[0];
      row = {
        id, tag: `L-${(layout.luminaires.length + 1).toString().padStart(2, "0")}`,
        type: t?.id || "led_2x4_5000lm", label: t?.label || "LED 2x4",
        lumens: t?.lumens || 5000, watts: t?.watts || 45,
        x_in: 24, y_in: 24, mount_in: (project.depth_in || 96) - 6, rule: "manual",
      };
    } else {
      const t = opts.switch_types?.[0];
      row = {
        id, tag: `SW-${(layout.switches.length + 1).toString().padStart(2, "0")}`,
        type: t?.id || "sp_20a", label: t?.label || "Single-pole 20A",
        amps: t?.amps || 20, volts: t?.volts || 120,
        x_in: 12, y_in: 12, mount_in: 48, rule: "manual",
      };
    }
    updateBucket(bucket, [...(layout[bucket] || []), row]);
  };

  const removeRow = (bucket, id) =>
    updateBucket(bucket, layout[bucket].filter((r) => r.id !== id));

  const editRow = (bucket, id, field, value) => {
    const items = layout[bucket].map((r) =>
      r.id !== id ? r : { ...r, [field]: (NUMERIC_FIELDS.includes(field) ? Number(value) || 0 : value) });
    updateBucket(bucket, items);
  };

  const editType = (bucket, id, newType) => {
    const t = (bucket === "receptacles" ? opts.receptacle_types
            : bucket === "luminaires"  ? opts.luminaire_types
            : opts.switch_types).find((x) => x.id === newType);
    if (!t) return;
    const patch = bucket === "luminaires"
      ? { type: newType, label: t.label, lumens: t.lumens, watts: t.watts }
      : { type: newType, label: t.label, amps: t.amps, volts: t.volts };
    updateBucket(bucket, layout[bucket].map((r) => r.id !== id ? r : { ...r, ...patch }));
  };

  const summary = useMemo(() => layout.summary || {}, [layout]);
  const hasAnyItem = !!(layout.receptacles?.length || layout.luminaires?.length || layout.switches?.length);

  return (
    <div data-testid="small-power-tab">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="label-tag">// 06 Small Power</div>
          <h2 className="font-display text-2xl font-bold inline-flex items-center gap-2">
            <Power size={26} /> Small Power · receptacles &amp; lighting
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Auto-install per <span className="text-[#00E5FF]">NEC 210.63 · 110.26(D) · 210.70</span> or <span className="text-[#00E5FF]">CEC 26-712 · 26-714 · 30-300</span>, then edit each item below.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            className="btn-ghost text-sm py-2 inline-flex items-center gap-2"
            onClick={() => setShowInfo(!showInfo)}
            data-testid="small-power-info-toggle"
          >
            <BookOpen size={16} /> Code refs
          </button>
          <button
            className="btn-ghost text-sm py-2 inline-flex items-center gap-2"
            onClick={clearAll}
            disabled={!hasAnyItem}
            data-testid="small-power-clear"
          >
            <Trash size={16} /> Clear all
          </button>
          <button
            className="btn-primary text-sm py-2 inline-flex items-center gap-2"
            onClick={runAutoInstall}
            disabled={busy || !roomReady}
            data-testid="small-power-auto-install"
          >
            <Robot size={16} weight="bold" /> {busy ? "Installing…" : "Auto-install per code"}
          </button>
        </div>
      </div>

      {!roomReady && (
        <div className="border border-yellow-500/40 bg-yellow-500/5 p-4 text-sm text-yellow-300 font-mono mb-4" data-testid="small-power-no-room">
          Set room length × width on the Size tab to enable auto-install.
        </div>
      )}

      {showInfo && <CodeRefsPanel />}

      <AutoInstallSettings
        code={code} setCode={setCode}
        spacing={spacing} setSpacing={setSpacing}
        targetFc={targetFc} setTargetFc={setTargetFc}
        withinFt={withinFt} setWithinFt={setWithinFt}
        mountIn={mountIn} setMountIn={setMountIn}
        replace={replace} setReplace={setReplace}
      />

      {/* Summary cards */}
      {hasAnyItem && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
          <SummaryCard label="Receptacles" value={summary.receptacle_count || layout.receptacles.length} testid="sp-stat-receptacles" />
          <SummaryCard label="Luminaires"  value={summary.luminaire_count  || layout.luminaires.length}  testid="sp-stat-luminaires"  />
          <SummaryCard label="Switches"    value={summary.switch_count     || layout.switches.length}    testid="sp-stat-switches"    />
          <SummaryCard label="Room ft²"    value={summary.area_ft2 ?? "—"}        testid="sp-stat-area" />
          <SummaryCard label="Target fc"   value={summary.target_fc ?? targetFc} testid="sp-stat-fc" />
        </div>
      )}

      {/* Citations + notes */}
      {layout.notes?.length > 0 && (
        <div className="border border-white/10 bg-[#0a0d12] p-3 mb-4 text-xs font-mono text-gray-300" data-testid="small-power-notes">
          {layout.notes.map((n, i) => <div key={`note-${i}-${(n || "").slice(0, 24)}`}>· {n}</div>)}
          {layout.citations?.length > 0 && (
            <div className="mt-2 text-[10px] text-gray-500 tracking-widest uppercase">
              Citations: {layout.citations.join(" · ")}
            </div>
          )}
        </div>
      )}

      <Section
        title="Receptacles"
        icon={<Power size={20} className="text-[#00E5FF]" />}
        onAdd={() => addRow("receptacles")}
        testid="sp-section-receptacles"
      >
        <ItemTable bucket="receptacles" rows={layout.receptacles} types={opts.receptacle_types}
          onEdit={editRow} onType={editType} onRemove={removeRow} columns={RECEP_COLS} />
      </Section>

      <Section
        title="Lighting fixtures"
        icon={<Lightbulb size={20} className="text-[#FFCC00]" />}
        onAdd={() => addRow("luminaires")}
        testid="sp-section-luminaires"
      >
        <ItemTable bucket="luminaires" rows={layout.luminaires} types={opts.luminaire_types}
          onEdit={editRow} onType={editType} onRemove={removeRow} columns={LUM_COLS} />
      </Section>

      <Section
        title="Switches"
        icon={<Lightning size={20} className="text-[#00E5FF]" />}
        onAdd={() => addRow("switches")}
        testid="sp-section-switches"
      >
        <ItemTable bucket="switches" rows={layout.switches} types={opts.switch_types}
          onEdit={editRow} onType={editType} onRemove={removeRow} columns={SW_COLS} />
      </Section>

      {roomReady && hasAnyItem && (
        <>
          <div className="flex items-center justify-between mt-6 mb-2">
            <div className="label-tag">// 07 Layout preview</div>
            <div className="inline-flex border border-white/10 rounded-none overflow-hidden text-[11px] font-mono uppercase tracking-wider" data-testid="sp-preview-mode-toggle">
              <button
                onClick={() => setPreviewMode("2d")}
                className={`px-3 py-1.5 transition-colors ${previewMode === "2d" ? "bg-[#00E5FF] text-black" : "text-gray-400 hover:text-cyan-300"}`}
                data-testid="sp-preview-mode-2d"
              >
                2D Plan
              </button>
              <button
                onClick={() => setPreviewMode("3d")}
                className={`px-3 py-1.5 transition-colors ${previewMode === "3d" ? "bg-[#00E5FF] text-black" : "text-gray-400 hover:text-cyan-300"}`}
                data-testid="sp-preview-mode-3d"
              >
                3D View
              </button>
            </div>
          </div>
          {previewMode === "2d"
            ? <SmallPowerPlan project={project} layout={layout} />
            : <SmallPower3D
                project={project} layout={layout}
                onItemEdit={(bucket, id, patch) => {
                  // patch is an object; replay each key through the existing editRow
                  Object.entries(patch).forEach(([field, value]) => {
                    editRow(bucket, id, field, value);
                  });
                }}
              />
          }
        </>
      )}
    </div>
  );
}
